import type { FC } from 'react';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

import { Box, xcss } from '@atlaskit/primitives';

import { ProfileAvatar } from '@confluence/profile';
import { getEmojiValueFromContent } from '@confluence/emoji-title/entry-points/transformer';
import { useElementSeenCallback } from '@confluence/analytics';

import { getContentLink, getSpaceLink, getUserAccountId } from '../feedCard-helpers';
import type { FeedPopularFeedQuery_popularFeed_nodes } from '../../__types__/FeedPopularFeedQuery';
import { Wrapper } from '../FeedCard/FeedCard';
import { FeedCardBody } from '../FeedCard/FeedCardBody';
import { FeedCardInvisibleFullSizedLink } from '../FeedCard/FeedCardInvisibleFullSizedLink';
import { FeedCardContext } from '../FeedCard/FeedCardContext';
import { useFeedCardAnalyticsEvents } from '../useFeedCardAnalyticsEvents';
import { useIsMounted } from '../useIsMounted';
import type { FilterByLabelFeedQuery_search_nodes } from '../../__types__/FilterByLabelFeedQuery';
import type { PopularFeedTimeGranularityValue } from '../../Feed';

const avatarWrapperStyles = xcss({
	paddingRight: 'space.200',
});

type FeedNodeType = FeedPopularFeedQuery_popularFeed_nodes | FilterByLabelFeedQuery_search_nodes;

type FeedCardProps = {
	feedItem: FeedNodeType;
	ranking: number;
	timeGranularity?: PopularFeedTimeGranularityValue;
	disableAnimation?: boolean;
};

export const PopularFeedCard: FC<FeedCardProps> = memo(
	({ feedItem, ranking, disableAnimation, timeGranularity }) => {
		const isMounted = useIsMounted();
		const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
		const bottomOfCardRef = useRef<HTMLDivElement | null>(null);
		const [hasBeenHovered, setHasBeenHovered] = useState(false);

		const authorName = feedItem?.content?.history?.createdBy?.displayName || '';
		const authorId = getUserAccountId(feedItem?.content?.history?.createdBy) || '';
		const authorSrc = feedItem?.content?.history?.createdBy?.profilePicture?.path || '';
		const ownerName = feedItem?.content?.history?.ownedBy?.displayName || '';
		const ownerId = getUserAccountId(feedItem?.content?.history?.ownedBy) || '';
		const ownerSrc = feedItem?.content?.history?.ownedBy?.profilePicture?.path || '';
		const contentUrl = getContentLink(feedItem.content);
		const contentId = feedItem?.content?.id || '';
		const contentType = feedItem?.content?.type || 'page';
		const contentSubType = feedItem?.content?.subType;
		const contentTitle = feedItem?.content?.title || '';
		const contentExcerpt = feedItem?.content?.excerpt;
		const creationDate = feedItem.content?.history?.createdDate || '';
		const spaceName = feedItem?.content?.space?.name || '';
		const spaceId = feedItem?.content?.space?.id || '';
		const spaceKey = feedItem?.content?.space?.key || undefined;
		const spaceUrl = getSpaceLink(feedItem.content);
		const numComments = feedItem?.content?.metadata?.comments?.commentsCount || 0;
		const emoji = getEmojiValueFromContent(feedItem?.content || {});
		const lastModifiedDate = feedItem?.content?.metadata?.lastModifiedDate || '';

		const handleMouseEnter = React.useCallback(() => {
			setHasBeenHovered(true);
		}, [setHasBeenHovered]);

		const fireAnalyticsEvent = useFeedCardAnalyticsEvents(contentId, spaceId, 'popular', {
			ranking,
			contentType,
			creationDate: new Date(creationDate).getTime(),
			commentsCount: numComments,
			popularFeedTimeGranularity: timeGranularity,
		});

		const onObserved = useCallback(() => {
			fireAnalyticsEvent({
				type: 'sendTrackEvent',
				action: 'observed',
				actionSubject: 'popular-feedCard',
			});
		}, [fireAnalyticsEvent]);

		useElementSeenCallback(bottomOfCardRef, onObserved);

		const feedCardContextValue = useMemo(() => ({ fireAnalyticsEvent }), [fireAnalyticsEvent]);

		if (!contentId) {
			return null;
		}

		// check if page owner exists and content is not a blog
		// checking against the name here for the case of anonymous users
		const shouldUsePageOwner = ownerName && contentType === 'page';

		return (
			<FeedCardContext.Provider value={feedCardContextValue}>
				<Wrapper
					data-testid="popular-feed-card"
					isActionMenuOpen={isActionMenuOpen}
					disableAnimation={isMounted || disableAnimation}
					onMouseEnter={handleMouseEnter}
					onFocus={handleMouseEnter}
				>
					<FeedCardInvisibleFullSizedLink
						href={contentUrl}
						popularFeedTimeGranularity={timeGranularity}
					/>
					<Box xcss={avatarWrapperStyles}>
						<ProfileAvatar
							userId={shouldUsePageOwner ? ownerId : authorId}
							src={shouldUsePageOwner ? ownerSrc : authorSrc}
							canViewProfile
							size="medium"
							name={shouldUsePageOwner ? ownerName : authorName}
						/>
					</Box>
					<FeedCardBody
						toggleActionMenu={setIsActionMenuOpen}
						emoji={emoji}
						contentId={contentId}
						contentTitle={contentTitle}
						contentType={contentType}
						contentSubType={contentSubType}
						contentUrl={contentUrl}
						authorName={authorName}
						authorId={authorId}
						ownerName={ownerName}
						ownerId={ownerId}
						spaceId={spaceId}
						spaceKey={spaceKey}
						spaceName={spaceName}
						spaceUrl={spaceUrl}
						contentExcerpt={contentExcerpt}
						numComments={numComments}
						popularCardLayout
						timestamp={creationDate}
						hasBeenHovered={hasBeenHovered}
						lastModifiedDate={lastModifiedDate}
						source="popular-feed"
						popularFeedTimeGranularity={timeGranularity}
					/>
					<div ref={bottomOfCardRef} />
				</Wrapper>
			</FeedCardContext.Provider>
		);
	},
);
